import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import WalletConnectionPanel from './components/WalletConnectionPanel';
import NetworkStatus from './components/NetworkStatus';
import ConnectionRequirements from './components/ConnectionRequirements';
import TrustSignals from '../../components/ui/TrustSignals';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const WalletConnection = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [connectionStep, setConnectionStep] = useState('connect'); // connect, verify, ready

  useEffect(() => {
    // Check for existing wallet connection on mount
    const checkExistingConnection = () => {
      const savedConnection = localStorage.getItem('wallet_connected');
      const savedWalletData = localStorage.getItem('wallet_data');
      
      if (savedConnection === 'true' && savedWalletData) {
        try {
          const data = JSON.parse(savedWalletData);
          setWalletData(data);
          setIsConnected(true);
          setConnectionStep('ready');
        } catch (error) {
          console.error('Failed to parse saved wallet data:', error);
          localStorage.removeItem('wallet_connected');
          localStorage.removeItem('wallet_data');
        }
      }
    };

    checkExistingConnection();
  }, []);

  const handleConnectionChange = (data) => {
    if (data) {
      setWalletData(data);
      setIsConnected(true);
      setConnectionStep('verify');
      
      // Save connection state
      localStorage.setItem('wallet_connected', 'true');
      localStorage.setItem('wallet_data', JSON.stringify(data));
      
      // Simulate verification process
      setTimeout(() => {
        setConnectionStep('ready');
      }, 2000);
    } else {
      setWalletData(null);
      setIsConnected(false);
      setConnectionStep('connect');
      
      // Clear saved state
      localStorage.removeItem('wallet_connected');
      localStorage.removeItem('wallet_data');
    }
  };

  const handleDisconnect = () => {
    handleConnectionChange(null);
  };

  const getStepStatus = () => {
    switch (connectionStep) {
      case 'connect':
        return {
          title: 'Connect Your Wallet',
          description: 'Choose a wallet provider to get started with blockchain notarization',
          icon: 'Wallet',
          color: 'text-primary'
        };
      case 'verify':
        return {
          title: 'Verifying Connection',
          description: 'Checking network compatibility and account status',
          icon: 'Loader2',
          color: 'text-primary animate-spin'
        };
      case 'ready':
        return {
          title: 'Ready to Notarize',
          description: 'Your wallet is connected and ready for blockchain notarization',
          icon: 'CheckCircle',
          color: 'text-success'
        };
      default:
        return {
          title: 'Connect Your Wallet',
          description: 'Choose a wallet provider to get started',
          icon: 'Wallet',
          color: 'text-primary'
        };
    }
  };

  const stepStatus = getStepStatus();

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full">
                <Icon name={stepStatus?.icon} size={40} className={stepStatus?.color} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {stepStatus?.title}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {stepStatus?.description}
            </p>
          </div>

          {/* Connection Success Banner */}
          {isConnected && connectionStep === 'ready' && (
            <div className="mb-8 p-6 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-full">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-success">Wallet Connected Successfully!</h3>
                    <p className="text-success/80">
                      Connected to {walletData?.address?.slice(0, 8)}...{walletData?.address?.slice(-6)} 
                      on {walletData?.network} network
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Link to="/webpage-notarization">
                    <Button variant="default" iconName="ArrowRight" iconPosition="right">
                      Start Notarizing
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    onClick={handleDisconnect}
                    iconName="LogOut"
                    iconPosition="left"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Connection Panel */}
            <div className="lg:col-span-2 space-y-6">
              <WalletConnectionPanel onConnectionChange={handleConnectionChange} />
              
              {/* Quick Actions */}
              {isConnected && connectionStep === 'ready' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link to="/webpage-notarization" className="block">
                      <div className="p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-smooth">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                            <Icon name="FileCheck" size={20} className="text-primary" />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground">Notarize Webpage</h4>
                            <p className="text-sm text-muted-foreground">Create blockchain proof</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                    
                    <Link to="/archive-details" className="block">
                      <div className="p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-smooth">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                            <Icon name="History" size={20} className="text-primary" />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground">View History</h4>
                            <p className="text-sm text-muted-foreground">Browse past records</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Status & Requirements */}
            <div className="space-y-6">
              <NetworkStatus isConnected={isConnected} walletData={walletData} />
              <ConnectionRequirements isConnected={isConnected} walletData={walletData} />
            </div>
          </div>

          {/* Trust Signals Section */}
          <div className="mt-12">
            <TrustSignals />
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <div className="text-center">
              <Icon name="HelpCircle" size={32} className="text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-muted-foreground mb-4">
                Having trouble connecting your wallet or switching networks?
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={() => window.open('https://docs.polynotary.com/wallet-setup', '_blank')}
                  iconName="Book"
                  iconPosition="left"
                >
                  Setup Guide
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open('https://support.polynotary.com', '_blank')}
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Get Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WalletConnection;